import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Loader2,
  Save,
  Eye,
  Plus,
  Trash2,
  Image as ImageIcon,
  Video,
  Quote,
  DollarSign,
  Star,
  Lock,
  ExternalLink,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PromoPageEditor = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tierFeatures, setTierFeatures] = useState(null);
  const [existingPage, setExistingPage] = useState(null);

  const [form, setForm] = useState({
    headline: '',
    tagline: '',
    description: '',
    cover_image_url: '',
    logo_url: '',
    services: [],
    video_url: '',
    testimonials: [],
    gallery_images: [],
    contact_email: '',
    contact_phone: '',
    website_url: '',
    social_links: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: ''
    },
    is_published: true,
    show_in_ads: true
  });

  const [newService, setNewService] = useState({
    title: '',
    description: '',
    price: '',
    price_type: 'fixed',
    image_url: '',
    is_featured: false
  });

  const [newTestimonial, setNewTestimonial] = useState({
    author_name: '',
    author_title: '',
    content: '',
    rating: 5,
    image_url: ''
  });

  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [testimonialDialogOpen, setTestimonialDialogOpen] = useState(false);
  const [newGalleryUrl, setNewGalleryUrl] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get tier features
      const featuresRes = await axios.get(`${API}/api/promo-pages/tier-features`);
      setTierFeatures(featuresRes.data);

      if (!featuresRes.data.has_access) {
        setLoading(false);
        return;
      }

      // Get existing page if any
      try {
        const pageRes = await axios.get(`${API}/api/promo-pages/my-page`);
        if (pageRes.data) {
          setExistingPage(pageRes.data);
          setForm({
            headline: pageRes.data.headline || '',
            tagline: pageRes.data.tagline || '',
            description: pageRes.data.description || '',
            cover_image_url: pageRes.data.cover_image_url || '',
            logo_url: pageRes.data.logo_url || '',
            services: pageRes.data.services || [],
            video_url: pageRes.data.video_url || '',
            testimonials: pageRes.data.testimonials || [],
            gallery_images: pageRes.data.gallery_images || [],
            contact_email: pageRes.data.contact_email || '',
            contact_phone: pageRes.data.contact_phone || '',
            website_url: pageRes.data.website_url || '',
            social_links: pageRes.data.social_links || {
              facebook: '',
              twitter: '',
              instagram: '',
              linkedin: ''
            },
            is_published: pageRes.data.is_published ?? true,
            show_in_ads: pageRes.data.show_in_ads ?? true
          });
        }
      } catch (e) {
        // No existing page
      }
    } catch (error) {
      toast.error('Failed to load page data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.headline || !form.description) {
      toast.error('Please fill in headline and description');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        services: form.services.map(s => ({
          ...s,
          price: s.price ? parseFloat(s.price) : null
        }))
      };

      if (existingPage) {
        await axios.put(`${API}/api/promo-pages`, payload);
        toast.success('Promo page updated!');
      } else {
        const response = await axios.post(`${API}/api/promo-pages`, payload);
        setExistingPage(response.data);
        toast.success('Promo page created!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addService = () => {
    if (!newService.title || !newService.description) {
      toast.error('Please fill in service title and description');
      return;
    }

    const maxServices = tierFeatures?.features?.max_services || 10;
    if (form.services.length >= maxServices) {
      toast.error(`Maximum ${maxServices} services allowed`);
      return;
    }

    setForm({
      ...form,
      services: [...form.services, { ...newService, id: Date.now().toString() }]
    });
    setNewService({
      title: '',
      description: '',
      price: '',
      price_type: 'fixed',
      image_url: '',
      is_featured: false
    });
    setServiceDialogOpen(false);
  };

  const removeService = (index) => {
    setForm({
      ...form,
      services: form.services.filter((_, i) => i !== index)
    });
  };

  const addTestimonial = () => {
    if (!newTestimonial.author_name || !newTestimonial.content) {
      toast.error('Please fill in author name and content');
      return;
    }

    const maxTestimonials = tierFeatures?.features?.max_testimonials || 0;
    if (form.testimonials.length >= maxTestimonials) {
      toast.error(`Maximum ${maxTestimonials} testimonials allowed`);
      return;
    }

    setForm({
      ...form,
      testimonials: [...form.testimonials, { ...newTestimonial, id: Date.now().toString() }]
    });
    setNewTestimonial({
      author_name: '',
      author_title: '',
      content: '',
      rating: 5,
      image_url: ''
    });
    setTestimonialDialogOpen(false);
  };

  const removeTestimonial = (index) => {
    setForm({
      ...form,
      testimonials: form.testimonials.filter((_, i) => i !== index)
    });
  };

  const addGalleryImage = () => {
    if (!newGalleryUrl) return;

    const maxGallery = tierFeatures?.features?.max_gallery || 0;
    if (form.gallery_images.length >= maxGallery) {
      toast.error(`Maximum ${maxGallery} gallery images allowed`);
      return;
    }

    setForm({
      ...form,
      gallery_images: [...form.gallery_images, newGalleryUrl]
    });
    setNewGalleryUrl('');
  };

  const removeGalleryImage = (index) => {
    setForm({
      ...form,
      gallery_images: form.gallery_images.filter((_, i) => i !== index)
    });
  };

  const isPremium = tierFeatures?.tier === 'premium';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!tierFeatures?.has_access) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          <Card className="max-w-lg mx-auto text-center p-8">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-heading mb-2">Upgrade Required</h2>
            <p className="text-muted-foreground mb-6">
              {tierFeatures?.upgrade_message || 'Pro or Premium membership required to create a promo page'}
            </p>
            <Button onClick={() => navigate('/membership')}>
              View Membership Options
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium">
              {existingPage ? 'Edit' : 'Create'} Your Promo Page
            </h1>
            <p className="text-muted-foreground">
              Showcase your services to the community
            </p>
          </div>
          <div className="flex gap-3">
            {existingPage && (
              <Button 
                variant="outline" 
                onClick={() => navigate(`/members/${user?.username}`)}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} data-testid="save-promo-btn">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Page
            </Button>
          </div>
        </div>

        {/* Tier Info */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className={isPremium ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-primary'}>
                  {tierFeatures.tier_name} Tier
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {tierFeatures.features.max_services} services
                  {isPremium && `, ${tierFeatures.features.max_testimonials} testimonials, ${tierFeatures.features.max_gallery} gallery images`}
                </span>
              </div>
              {!isPremium && (
                <Button variant="outline" size="sm" onClick={() => navigate('/membership')}>
                  Upgrade to Premium
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="headline">Headline *</Label>
                  <Input
                    id="headline"
                    placeholder="e.g., Professional Dog Training Services"
                    value={form.headline}
                    onChange={(e) => setForm({ ...form, headline: e.target.value })}
                    maxLength={150}
                    data-testid="promo-headline"
                  />
                </div>
                <div>
                  <Label htmlFor="tagline">Tagline</Label>
                  <Input
                    id="tagline"
                    placeholder="e.g., 15 years of experience helping dogs and their owners"
                    value={form.tagline}
                    onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                    maxLength={250}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Tell visitors about yourself and your services..."
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={5}
                    maxLength={2000}
                    data-testid="promo-description"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Images */}
            <Card>
              <CardHeader>
                <CardTitle>Images</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="logo_url">Logo URL</Label>
                  <Input
                    id="logo_url"
                    placeholder="https://example.com/logo.png"
                    value={form.logo_url}
                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="cover_image_url">Cover Image URL</Label>
                  <Input
                    id="cover_image_url"
                    placeholder="https://example.com/cover.jpg"
                    value={form.cover_image_url}
                    onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Services */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Services & Products ({form.services.length}/{tierFeatures.features.max_services})
                </CardTitle>
                <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-service-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Service/Product</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <Input
                        placeholder="Title *"
                        value={newService.title}
                        onChange={(e) => setNewService({ ...newService, title: e.target.value })}
                      />
                      <Textarea
                        placeholder="Description *"
                        value={newService.description}
                        onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                        rows={3}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          placeholder="Price"
                          type="number"
                          value={newService.price}
                          onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                        />
                        <Select 
                          value={newService.price_type} 
                          onValueChange={(val) => setNewService({ ...newService, price_type: val })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Fixed Price</SelectItem>
                            <SelectItem value="hourly">Per Hour</SelectItem>
                            <SelectItem value="starting_at">Starting At</SelectItem>
                            <SelectItem value="contact">Contact for Price</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Input
                        placeholder="Image URL (optional)"
                        value={newService.image_url}
                        onChange={(e) => setNewService({ ...newService, image_url: e.target.value })}
                      />
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={newService.is_featured}
                          onCheckedChange={(checked) => setNewService({ ...newService, is_featured: checked })}
                        />
                        <Label>Featured Service</Label>
                      </div>
                      <Button onClick={addService} className="w-full">Add Service</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {form.services.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No services added yet. Click "Add" to add your first service.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {form.services.map((service, idx) => (
                      <div key={service.id || idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{service.title}</p>
                            {service.is_featured && (
                              <Badge variant="secondary" className="text-xs">Featured</Badge>
                            )}
                          </div>
                          {service.price && (
                            <p className="text-sm text-muted-foreground">
                              {service.price_type === 'hourly' ? `$${service.price}/hr` : 
                               service.price_type === 'starting_at' ? `From $${service.price}` :
                               `$${service.price}`}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeService(idx)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Premium: Video */}
            <Card className={!isPremium ? 'opacity-60' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Featured Video
                  {!isPremium && <Badge variant="outline">Premium</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isPremium ? (
                  <div>
                    <Label htmlFor="video_url">YouTube Video URL</Label>
                    <Input
                      id="video_url"
                      placeholder="https://youtube.com/watch?v=..."
                      value={form.video_url}
                      onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    />
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    <Lock className="w-4 h-4 inline mr-2" />
                    Upgrade to Premium to add a featured video
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Premium: Testimonials */}
            <Card className={!isPremium ? 'opacity-60' : ''}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Quote className="w-5 h-5" />
                  Testimonials
                  {!isPremium && <Badge variant="outline">Premium</Badge>}
                  {isPremium && <span className="text-sm font-normal text-muted-foreground">
                    ({form.testimonials.length}/{tierFeatures.features.max_testimonials})
                  </span>}
                </CardTitle>
                {isPremium && (
                  <Dialog open={testimonialDialogOpen} onOpenChange={setTestimonialDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Testimonial</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <Input
                          placeholder="Author Name *"
                          value={newTestimonial.author_name}
                          onChange={(e) => setNewTestimonial({ ...newTestimonial, author_name: e.target.value })}
                        />
                        <Input
                          placeholder="Author Title (e.g., Happy Customer)"
                          value={newTestimonial.author_title}
                          onChange={(e) => setNewTestimonial({ ...newTestimonial, author_title: e.target.value })}
                        />
                        <Textarea
                          placeholder="Testimonial content *"
                          value={newTestimonial.content}
                          onChange={(e) => setNewTestimonial({ ...newTestimonial, content: e.target.value })}
                          rows={4}
                        />
                        <div>
                          <Label>Rating</Label>
                          <div className="flex gap-1 mt-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setNewTestimonial({ ...newTestimonial, rating: star })}
                              >
                                <Star className={`w-6 h-6 ${
                                  star <= newTestimonial.rating 
                                    ? 'fill-amber-400 text-amber-400' 
                                    : 'text-muted-foreground/30'
                                }`} />
                              </button>
                            ))}
                          </div>
                        </div>
                        <Input
                          placeholder="Author Photo URL (optional)"
                          value={newTestimonial.image_url}
                          onChange={(e) => setNewTestimonial({ ...newTestimonial, image_url: e.target.value })}
                        />
                        <Button onClick={addTestimonial} className="w-full">Add Testimonial</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {!isPremium ? (
                  <p className="text-muted-foreground">
                    <Lock className="w-4 h-4 inline mr-2" />
                    Upgrade to Premium to add customer testimonials
                  </p>
                ) : form.testimonials.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No testimonials added yet. Click "Add" to add your first testimonial.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {form.testimonials.map((testimonial, idx) => (
                      <div key={testimonial.id || idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium">{testimonial.author_name}</p>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            "{testimonial.content}"
                          </p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeTestimonial(idx)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Premium: Gallery */}
            <Card className={!isPremium ? 'opacity-60' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Photo Gallery
                  {!isPremium && <Badge variant="outline">Premium</Badge>}
                  {isPremium && <span className="text-sm font-normal text-muted-foreground">
                    ({form.gallery_images.length}/{tierFeatures.features.max_gallery})
                  </span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!isPremium ? (
                  <p className="text-muted-foreground">
                    <Lock className="w-4 h-4 inline mr-2" />
                    Upgrade to Premium to add a photo gallery
                  </p>
                ) : (
                  <>
                    <div className="flex gap-2 mb-4">
                      <Input
                        placeholder="Image URL"
                        value={newGalleryUrl}
                        onChange={(e) => setNewGalleryUrl(e.target.value)}
                      />
                      <Button onClick={addGalleryImage}>Add</Button>
                    </div>
                    {form.gallery_images.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {form.gallery_images.map((url, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={url} 
                              alt={`Gallery ${idx + 1}`}
                              className="w-full aspect-square object-cover rounded-lg"
                            />
                            <Button
                              variant="destructive"
                              size="icon"
                              className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => removeGalleryImage(idx)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="contact_email">Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="contact@example.com"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone">Phone</Label>
                  <Input
                    id="contact_phone"
                    placeholder="(555) 123-4567"
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="website_url">Website</Label>
                  <Input
                    id="website_url"
                    placeholder="https://yourwebsite.com"
                    value={form.website_url}
                    onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Social Links */}
            <Card>
              <CardHeader>
                <CardTitle>Social Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {['facebook', 'twitter', 'instagram', 'linkedin'].map((platform) => (
                  <div key={platform}>
                    <Label htmlFor={platform} className="capitalize">{platform}</Label>
                    <Input
                      id={platform}
                      placeholder={`https://${platform}.com/yourpage`}
                      value={form.social_links[platform] || ''}
                      onChange={(e) => setForm({ 
                        ...form, 
                        social_links: { ...form.social_links, [platform]: e.target.value }
                      })}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Published</Label>
                    <p className="text-sm text-muted-foreground">
                      Make your page visible to others
                    </p>
                  </div>
                  <Switch
                    checked={form.is_published}
                    onCheckedChange={(checked) => setForm({ ...form, is_published: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Show in Ads</Label>
                    <p className="text-sm text-muted-foreground">
                      Display in sidebar and featured sections
                    </p>
                  </div>
                  <Switch
                    checked={form.show_in_ads}
                    onCheckedChange={(checked) => setForm({ ...form, show_in_ads: checked })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Page URL */}
            {existingPage && (
              <Card>
                <CardHeader>
                  <CardTitle>Your Page URL</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                    <code className="text-sm flex-1 truncate">
                      /members/{user?.username}
                    </code>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/members/${user?.username}`);
                        toast.success('Link copied!');
                      }}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoPageEditor;
